import React, { useCallback, useState, FC } from 'react';
import { useAvailableFirstTimeFreeCoupon } from '../../../../../hooks/api/coupon';
import { useReservationStats } from '../../../../../hooks/api/reservation-stats';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { CreditCard } from '../../../../../interfaces/creditCard';
import {
  CardSelection,
  PaymentMethod,
  SettingPayment,
} from '../../../../../interfaces/merchants/resources/books';
import { useAppDispatch } from '../../../../../redux';
import {
  inputPayment,
  RestartReservationError,
} from '../../../../../redux/actions/merchants/resources/books/inputs';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';
import { getMe } from '../../../../../redux/selectors/users';
import InputPayment, { PaymentMethodError } from './components/InputPayment';

type Props = {
  settings: SettingPayment;
  merchantPublicId: string;
  postable: boolean;
  onSubmit: () => void;
  onRestart: () => void;
};

export const InputPaymentOrganism: FC<Props> = ({
  settings,
  merchantPublicId,
  postable,
  onSubmit,
  onRestart,
}) => {
  const { inputs, loginUser } = useShallowEqualSelector(
    (state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
      loginUser: getMe(state.users),
    }),
  );
  const userEmail = inputs.userInfo?.customerFields?.['email']
    ? inputs.userInfo?.customerFields['email']
    : loginUser.email;

  const { reservationStats } = useReservationStats({
    merchantPublicId,
    email: userEmail,
  });
  const { data: firstTimeFreeCoupon } = useAvailableFirstTimeFreeCoupon();
  const isAvailableFirstTimeFreeCoupon =
    !!reservationStats?.isFirstTimeReservation && !!firstTimeFreeCoupon;
  const [paymentMethodError, setPaymentMethodError] = useState<
    PaymentMethodError | undefined
  >(undefined);

  const handleError = useCallback((error: PaymentMethodError) => {
    setPaymentMethodError(error);
  }, []);

  const dispatch = useAppDispatch();
  const handleSubmit = useCallback(
    async (params: {
      cardSelection?: CardSelection;
      subscriptionSelection?: string;
      ticketSelection?: string;
      creditCard?: CreditCard;
      firstTimeFreeCouponPublicId?: string;
      paymentMethod: PaymentMethod;
      email: string;
    }) => {
      const {
        cardSelection,
        subscriptionSelection,
        ticketSelection,
        creditCard,
        firstTimeFreeCouponPublicId,
        paymentMethod,
        email,
      } = params;
      try {
        const { errorType } = await inputPayment({
          reservationId: inputs.reservationId!,
          payment: {
            cardSelection,
            subscriptionSelection,
            ticketSelection,
            creditCard,
            paymentMethod,
            firstTimeFreeCouponPublicId,
          },
          merchantPublicId,
          email,
          dispatch,
        });
        if (errorType) {
          handleError(errorType);
        } else {
          onSubmit();
        }
      } catch (e) {
        if (e instanceof RestartReservationError) {
          onRestart();
        }
      }
    },
    [dispatch, inputs, merchantPublicId, onSubmit, onRestart, handleError],
  );
  return (
    <InputPayment
      settings={settings}
      merchantPublicId={merchantPublicId}
      postable={postable}
      loginUser={loginUser}
      onSubmit={handleSubmit}
      inputs={inputs}
      isAvailabelFirstTimeFreeCoupon={isAvailableFirstTimeFreeCoupon}
      firstTimeFreeCoupon={firstTimeFreeCoupon}
      userEmail={userEmail}
      error={paymentMethodError}
    />
  );
};
